import { selectApiData } from 'util/selector/apiSelector';
import { selectErrorCode, selectIsAxiosError } from 'util/selector/axiosSelector';

import { WithData } from 'api/interfaces';
import { AxiosInstance } from 'axios';
import { Exception } from 'exception/Exception';
import { HttpException } from 'exception/HttpException';
import { UserUnauthorizedException } from 'exception/UserUnauthorizedException';
import { StatusCodes } from 'http-status-codes';
import { CircleModel } from 'api/dto/Circle.dto';

import { JWTUserDto } from './dto/JWTUser.dto';

export namespace AuthApi {
  export type ResetPasswordEntry = {
    name: string;
    logo?: string;
  };

  export type ResetPasswordRequestParams = {
    email: string;
    currentCircle: CircleModel;
  };
}

export class AuthApi {
  constructor(private api: AxiosInstance) {}

  logged(): Promise<JWTUserDto> {
    return this.api
      .get<WithData<JWTUserDto>>('/api/auth/user')
      .then(selectApiData)
      .catch((error: Exception) => {
        if (selectIsAxiosError(error)) {
          switch (selectErrorCode(error)) {
            case StatusCodes.UNAUTHORIZED:
              throw UserUnauthorizedException.createFromAxiosError(error);
            default:
              throw HttpException.createFromAxiosError(error);
          }
        }

        throw error;
      });
  }

  fetchIntercomToken(): Promise<string | undefined> {
    return this.api
      .get<WithData<{ intercomUserJwt: string | null }>>('/api/auth/intercom-token')
      .then(selectApiData)
      .then((data) => data.intercomUserJwt ?? undefined);
  }

  async logout(): Promise<void> {
    await this.api.post('/api/auth/logout');
  }

  resetPasswordRequest({
    email,
    currentCircle,
  }: AuthApi.ResetPasswordRequestParams): Promise<unknown> {
    if (!currentCircle) {
      return this.api.post(`/api/user/reset-password-request/${email}`).then(selectApiData);
    }

    const data = {
      name: currentCircle.name,
      logo: currentCircle.emailLogoUrl,
    };

    return this.api
      .post<WithData<AuthApi.ResetPasswordEntry>>(`/api/user/reset-password-request/${email}`, data)
      .then(selectApiData);
  }
}
